import React, { type FC } from "react";

import { useScoped } from "~/tools/impulse";
import { isTruthy } from "~/tools/is-truthy";

import { isHealthStateUnHealthy } from "../../../../common/functions";
import type { MonitorCheckStatus } from "../../../../common/openapi";
import { Tab, Tabs } from "../../../../components/react/tabs/Tabs";
import { useQueryMonitorCheckStatusRelatedFailures } from "../../../../queries";

import type { CheckStateChain, MonitorStatusDrawerTabType } from "./MonitorStatusDrawer/monitor-status-drawer-state";
import { useMonitorStatusDrawerContext } from "./MonitorStatusDrawer/use-monitor-status-drawer-context";
import { MonitorRelatedIssues } from "./MonitorRelatedIssues";
import { MonitorStatusDrawerMetrics } from "./MonitorStatusDrawerMetrics";

interface MonitorStatusDrawerTabsProps {
	checkStateChain: CheckStateChain;
	checkState: MonitorCheckStatus;
}

export const MonitorStatusDrawerTabs: FC<MonitorStatusDrawerTabsProps> = ({ checkStateChain, checkState }) => {
	const { timestamp } = useMonitorStatusDrawerContext();
	const timestampValue = useScoped(timestamp);
	const chainedCheckStateValue = useScoped((scope) => checkStateChain.peek(scope));
	const activeTab = useScoped(chainedCheckStateValue.selectedTab);

	const { data: checkStatusesLength = 0 } = useQueryMonitorCheckStatusRelatedFailures(
		{
			identifier: checkState.identifier,
			timestamp: timestampValue,
		},
		{
			select: ({ checkStatuses }) => checkStatuses.length,
		},
	);

	const showMetrics = checkState.metrics.length > 0;
	const showRelatedIssues = isHealthStateUnHealthy(checkState.health) && checkStatusesLength > 0;

	return (
		<Tabs<MonitorStatusDrawerTabType>
			activeKey={activeTab}
			className="text-gray-800"
			onChange={(tab) => chainedCheckStateValue.selectedTab.setValue(tab)}
			renderTabBar={(props, DefaultTabBar) => {
				if ([showMetrics, showRelatedIssues].filter(isTruthy).length <= 1) {
					// if only one tab is available, hide the tab bar
					return <React.Fragment />;
				}
				// otherwise, render the default tab bar
				return <DefaultTabBar {...props} />;
			}}
			tabBarStyle={{
				marginBottom: 24,
			}}
		>
			{showMetrics && (
				<Tab
					className="overflow-y-hidden"
					key="supporting-metrics"
					tab={`Supporting metrics (${checkState.metrics.length})`}
				>
					<MonitorStatusDrawerMetrics metrics={checkState.metrics} />
				</Tab>
			)}
			{showRelatedIssues && (
				<Tab key="related-issues" tab={`Related health violations (${checkStatusesLength})`}>
					<MonitorRelatedIssues checkState={checkState} checkStateChain={checkStateChain} />
				</Tab>
			)}
		</Tabs>
	);
};
